/**
 * « CONTENUS » — le manuel, chapitre par chapitre : ce qui est servi aux élèves
 * et ce qu'un humain a relu, EXERCICES COMPRIS. Refonte de la console, lot L2
 * (maquette `docs/pilotage/console-refonte/maquettes/02-contenus.html`).
 *
 * Remplace, sous l'entrée « Contenus » du menu, la longue liste de
 * « Programme » : 73 lignes sans filtre ni avancement, chacune « ✓ Publié ».
 * `/programme` reste servie à l'identique.
 *
 * Une lecture : `GET /v1/tuteur/manuel/chapitres?relecture=1` (les comptes par
 * chapitre viennent du serveur, en une réponse). Un serveur d'avant ne rend
 * pas les comptes : l'écran le DIT, et montre la liste quand même.
 *
 * Tout ce qui se calcule est dans `lib/contenus-format.ts`.
 */
import type {
  ChapitreProgrammeResumeDto,
  ComptesRelectureDto,
  Matiere,
  NiveauScolaire,
} from '@silex/shared';
import { useMemo } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';

import { api } from '../api/client';
import { Badge } from '../components/Badge';
import { Chargement, ErreurRessource } from '../components/EtatRessource';
import { Pagination, usePageDansAdresse } from '../components/Pagination';
import {
  MATIERES_FILTRE,
  NIVEAUX_FILTRE,
  NOM_SORTE,
  SORTES_CONTENU,
  adresseRelectureExercices,
  direACorriger,
  etatDeRelecture,
  filtrerChapitres,
  nombre,
  nonRelus,
  relusSurTotal,
  totauxDeRelecture,
  type FiltresContenus,
} from '../lib/contenus-format';
import { manuelVivantActif } from '../lib/manuel-vivant';
import { paginer } from '../lib/pagination';
import { direHorsService } from '../lib/relecture-exercices';
import { situerChapitre, statutChapitre } from '../lib/programme-format';
import { useAsync } from '../lib/useAsync';

import './contenus.css';

/** Les filtres vivent dans l'adresse : un lien « 3e sec., maths » s'envoie. */
function lireFiltres(p: URLSearchParams): FiltresContenus {
  return {
    niveau: (p.get('niveau') ?? '') as NiveauScolaire | '',
    matiere: (p.get('matiere') ?? '') as Matiere | '',
    socle: p.get('socle') === '1',
    recherche: p.get('q') ?? '',
  };
}

function Barre({ c }: { readonly c: ComptesRelectureDto }) {
  const part = (n: number): string => (c.total === 0 ? '0%' : `${(100 * n) / c.total}%`);
  return (
    <span className="contenus-barre" aria-hidden="true">
      <span className="contenus-barre__relus" style={{ width: part(c.relus) }} />
      <span className="contenus-barre__corriger" style={{ width: part(c.aCorriger) }} />
    </span>
  );
}

function CelluleSorte({ c }: { readonly c: ComptesRelectureDto | undefined }) {
  if (c === undefined) return <span className="contenus-muet">—</span>;
  const corriger = direACorriger(c);
  return (
    <span className="contenus-cellule">
      <span className="contenus-mono">{relusSurTotal(c)}</span>
      {corriger ? <span className="contenus-corriger">{corriger}</span> : null}
    </span>
  );
}

/**
 * Les filtres en paramètres d'adresse. La page N'Y EST PAS : changer de filtre
 * ramène à la page 1 (AB#880).
 */
export function adresseDesFiltres(f: FiltresContenus): URLSearchParams {
  const p = new URLSearchParams();
  if (f.niveau) p.set('niveau', f.niveau);
  if (f.matiere) p.set('matiere', f.matiere);
  if (f.socle) p.set('socle', '1');
  if (f.recherche) p.set('q', f.recherche);
  return p;
}

/**
 * Le tableau, sans chargement : éprouvé seul (`contenus-page.test.tsx`).
 * `chapitres` est la liste FILTRÉE entière ; le tableau en montre une page
 * (AB#880) — `page` hors bornes est ramenée à la plus proche.
 */
export function TableauContenus({
  chapitres: retenus,
  total,
  page: pageDemandee = 1,
  onPage = () => undefined,
}: {
  readonly chapitres: readonly ChapitreProgrammeResumeDto[];
  readonly total: number;
  readonly page?: number;
  readonly onPage?: (page: number) => void;
}) {
  const decoupe = paginer(retenus, pageDemandee);
  const chapitres = decoupe.elements;
  return (
    <>
      {/* AB#888 : les boutons aussi AU-DESSUS — sans dérouler 20 lignes. */}
      <Pagination
        page={decoupe}
        singulier="chapitre"
        onPage={onPage}
        testId="contenus-pages-haut"
        enTete
      />
      <div className="table-wrap">
        <table className="data-table" data-testid="contenus-chapitres">
          <thead>
            <tr>
              <th scope="col">Chapitre</th>
              <th scope="col" style={{ width: 230 }}>
                Exercices relus
              </th>
              <th scope="col">Fiches</th>
              <th scope="col">Capsules</th>
              <th scope="col">Erreurs</th>
              <th scope="col">État</th>
              <th scope="col">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {chapitres.map((c) => {
              const r = c.relecture;
              const etat = etatDeRelecture(r);
              const service = statutChapitre(c.statut);
              return (
                <tr key={c.code} data-code={c.code}>
                  <td>
                    <Link
                      to={`/programme/${encodeURIComponent(c.code)}`}
                      className="contenus-titre"
                    >
                      {c.titre}
                    </Link>
                    <div className="cell-sub">{situerChapitre(c.matiere, c.niveau, c.nature)}</div>
                  </td>
                  <td>
                    {r === undefined ? (
                      <span className="contenus-muet">—</span>
                    ) : (
                      <span className="contenus-exercices">
                        <Barre c={r.exercices} />
                        <CelluleSorte c={r.exercices} />
                        {/* Un exercice retiré par l'admin (15 oct. 2026) se voit ici. */}
                        {direHorsService(r.exercices) ? (
                          <span className="contenus-corriger" data-testid="contenus-hors-service">
                            {direHorsService(r.exercices)}
                          </span>
                        ) : null}
                      </span>
                    )}
                  </td>
                  <td>
                    <CelluleSorte c={r?.fiches} />
                  </td>
                  <td>
                    <CelluleSorte c={r?.capsules} />
                  </td>
                  <td>
                    <CelluleSorte c={r?.erreurs} />
                  </td>
                  <td>
                    <span className="contenus-etat">
                      <Badge tone={service.tone} label={service.label} />
                      {etat ? <Badge tone={etat.tone} label={etat.label} /> : null}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <Link className="btn btn--ghost btn--sm" to={adresseRelectureExercices(c.code)}>
                      Relire
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pagination page={decoupe} singulier="chapitre" onPage={onPage} testId="contenus-pages" />
      <p className="note-line" data-testid="contenus-compte">
        {retenus.length} chapitre{retenus.length > 1 ? 's' : ''} retenu
        {retenus.length > 1 ? 's' : ''} par les filtres, sur {total}.
      </p>
    </>
  );
}

export function ContenusPage() {
  const actif = manuelVivantActif();
  const loader = useMemo(
    () => (actif ? () => api.programmeChapitres({ relecture: true }) : null),
    [actif],
  );
  const liste = useAsync(loader);
  const [params, setParams] = useSearchParams();
  const filtres = lireFiltres(params);
  const { page, allerA, ancre } = usePageDansAdresse();

  /* Sans `page` : un filtre neuf repart de la page 1. */
  const poser = (patch: Partial<FiltresContenus>): void => {
    setParams(adresseDesFiltres({ ...filtres, ...patch }), { replace: true });
  };

  if (!actif) return <Navigate to="/" replace />;

  const affiches = liste.status === 'ready' ? filtrerChapitres(liste.data.chapitres, filtres) : [];
  const totaux = liste.status === 'ready' ? totauxDeRelecture(affiches) : null;

  return (
    <div className="content__inner">
      <div className="page-head">
        <div>
          <h1>Contenus</h1>
          <p className="page-sub">
            Le manuel, chapitre par chapitre : ce qui est servi aux élèves, et ce qu’un humain a
            relu.
          </p>
        </div>
        <Link
          className="btn btn--ghost btn--sm"
          to="/contenus/editeur-figure"
          data-testid="contenus-editeur-figure"
        >
          Éditeur de figure
        </Link>
        <input
          className="control"
          type="search"
          aria-label="Chercher un chapitre"
          placeholder="Chercher un chapitre…"
          value={filtres.recherche}
          onChange={(e) => poser({ recherche: e.target.value })}
          style={{ width: 240, minHeight: 40 }}
        />
      </div>

      {liste.status === 'loading' ? <Chargement quoi="les chapitres" /> : null}
      {liste.status === 'error' ? <ErreurRessource message={liste.message} /> : null}

      {liste.status === 'ready' ? (
        <>
          {totaux ? (
            <div className="contenus-totaux" data-testid="contenus-totaux">
              {SORTES_CONTENU.map((s) => (
                <div key={s} className="card contenus-total">
                  <div className="contenus-total__sorte">{NOM_SORTE[s]}</div>
                  <div className="contenus-total__nombre">{nombre(totaux[s].enService)}</div>
                  <div className="note-line">
                    en service · <b>{nombre(totaux[s].relus)} relus</b>
                    {totaux[s].aCorriger > 0 ? ` · ${nombre(totaux[s].aCorriger)} à corriger` : ''}
                    {` · ${nombre(nonRelus(totaux[s]))} à relire`}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="callout" role="status" data-testid="contenus-sans-comptes">
              Les comptes de relecture ne sont pas rendus par ce serveur : la liste est complète,
              l’avancement viendra avec sa mise à jour.
            </div>
          )}

          <div className="filters" role="group" aria-label="Filtrer les chapitres" ref={ancre}>
            {NIVEAUX_FILTRE.map((n) => (
              <button
                key={n.v || 'tous'}
                type="button"
                className={filtres.niveau === n.v ? 'chip-filter is-on' : 'chip-filter'}
                aria-pressed={filtres.niveau === n.v}
                onClick={() => poser({ niveau: n.v })}
              >
                {n.label}
              </button>
            ))}
            <span className="contenus-muet">·</span>
            {MATIERES_FILTRE.map((m) => (
              <button
                key={m.v || 'toutes'}
                type="button"
                className={filtres.matiere === m.v ? 'chip-filter is-on' : 'chip-filter'}
                aria-pressed={filtres.matiere === m.v}
                onClick={() => poser({ matiere: m.v })}
              >
                {m.label}
              </button>
            ))}
            <label className="contenus-socle">
              <input
                type="checkbox"
                checked={filtres.socle}
                onChange={(e) => poser({ socle: e.target.checked })}
              />{' '}
              Montrer les prérequis partagés (socle)
            </label>
          </div>

          <TableauContenus
            chapitres={affiches}
            total={liste.data.chapitres.length}
            page={page}
            onPage={allerA}
          />
        </>
      ) : null}
    </div>
  );
}
